import * as React from "react";
import { useTheme } from "next-themes";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Info,
  Loader2,
  X,
} from "lucide-react";
import { createPortal } from "react-dom";
import { toast, Toaster as Sonner } from "sonner";

type ToasterProps = React.ComponentProps<typeof Sonner>;

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme();

  const [hasToasts, setHasToasts] = React.useState(false);

  const computeHasToasts = React.useCallback(() => {
    const container =
      (document.querySelector("[data-sonner-toaster]") as HTMLElement | null) ||
      (document.querySelector(".toaster") as HTMLElement | null);

    if (!container) return false;

    const toasts = Array.from(
      container.querySelectorAll<HTMLElement>("[data-sonner-toast], .toast"),
    );

    return toasts.some((t) => {
      const state = (t.getAttribute("data-state") || "").toLowerCase();
      if (
        state === "closed" ||
        state === "dismissed" ||
        state === "removed" ||
        state === "unmounted"
      )
        return false;
      if (t.getAttribute("aria-hidden") === "true") return false;

      const style = window.getComputedStyle(t);
      if (style.display === "none") return false;
      if (style.visibility === "hidden") return false;
      if (style.opacity === "0") return false;

      if (t.offsetHeight === 0 || t.offsetWidth === 0) return false;

      return true;
    });
  }, []);

  const handleDismissAll = React.useCallback(() => {
    toast.dismiss();
    setHasToasts(false);
  }, []);

  const preventOutsideInteraction = React.useCallback(
    (e: React.SyntheticEvent) => {
      e.preventDefault();
      e.stopPropagation();
    },
    [],
  );

  React.useEffect(() => {
    let rafId = 0;
    let observer: MutationObserver | null = null;
    let intervalId: number | undefined;

    const tryAttach = () => {
      const container =
        (document.querySelector(
          "[data-sonner-toaster]",
        ) as HTMLElement | null) ||
        (document.querySelector(".toaster") as HTMLElement | null);

      if (!container) {
        rafId = window.requestAnimationFrame(tryAttach);
        return;
      }

      const update = () => {
        setHasToasts(computeHasToasts());
      };

      update();

      // Sonner pode animar/auto-remover sem disparar mutation em alguns cenários;
      // essa checagem garante que o botão some assim que não houver mais toasts.
      intervalId = window.setInterval(update, 500);

      observer = new MutationObserver(update);
      observer.observe(container, {
        childList: true,
        attributes: true,
        attributeFilter: ["data-state", "style", "class", "aria-hidden"],
        subtree: true,
      });
    };

    tryAttach();

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      observer?.disconnect();
      if (intervalId) window.clearInterval(intervalId);
    };
  }, [computeHasToasts]);

  return (
    <>
      {hasToasts &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="fixed right-0 top-0 z-[2147483647] w-full p-4 pointer-events-auto"
            style={{ zIndex: 2147483647, pointerEvents: "auto" }}
            onPointerDownCapture={preventOutsideInteraction}
            onMouseDownCapture={preventOutsideInteraction}
          >
            <div className="flex w-full justify-end">
              <button
                type="button"
                onPointerDownCapture={preventOutsideInteraction}
                onMouseDownCapture={preventOutsideInteraction}
                onClick={handleDismissAll}
                className="pointer-events-auto inline-flex items-center gap-2 rounded-md border border-border/60 bg-background/95 px-3 py-2 text-xs font-medium text-foreground shadow-md backdrop-blur hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <X className="h-4 w-4" />
                Fechar todas
              </button>
            </div>
          </div>,
          document.body,
        )}
      <Sonner
        theme={theme as ToasterProps["theme"]}
        expand
        gap={10}
        visibleToasts={4}
        icons={{
          success: <CheckCircle2 className="h-5 w-5 text-emerald-600" />,
          error: <AlertCircle className="h-5 w-5 text-destructive" />,
          warning: <AlertTriangle className="h-5 w-5 text-amber-600" />,
          info: <Info className="h-5 w-5 text-primary" />,
          loading: (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          ),
        }}
        className="toaster group"
        closeButton={false}
        style={{ top: 56, right: 40 }}
        toastOptions={{
          classNames: {
            toast:
              "toast w-[380px] max-w-[calc(100vw-2rem)] bg-transparent border-0 shadow-none p-0 ring-0",
            content: "p-0",
            icon: "hidden",
            title: "hidden",
            description: "hidden",
            actionButton: "hidden",
            cancelButton: "hidden",
            closeButton: "hidden",
          },
        }}
        {...props}
      />
    </>
  );
};

export { Toaster };
